/**
 * Canonical resolver for "which Altelix account does this statement belong to?".
 *
 * SINGLE SOURCE OF TRUTH. This logic previously lived in six hand-maintained
 * copies (parse-bank-statement, altelix-accounts, autoFlow, recovery,
 * originBuckets, agent-1-runner). Three carried the 2026-06-24 fix and three
 * did not, and that divergence is precisely why a targeted patch failed to stop
 * the bug: the ingest path rewrote the filename to a synthetic
 * "<trailing-4-digits>.pdf" BEFORE handing it to a corrected extractor, so
 * every "<ACCOUNT> <MMYY>.pdf" statement bound the reporting PERIOD as the
 * account (254 rows across Apr–Jun 2026, 152 already posted to QuickBooks).
 *
 * The anti-drift guard in `__tests__/statement-filename.test.ts` fails the
 * build if a seventh copy appears.
 *
 * Deliberately dependency-free (no `node:path`, no imports at all) so the agent
 * runners, the Vite frontend bundle and the Convex bundle can all share it.
 */

/**
 * Every account Altelix reconciles, by 4-digit suffix: five bank accounts plus
 * the credit cards 0517 (BOA) and 1003 (Amex). Adding a real account here is
 * a prerequisite for its statements to auto-bind — see the "never invent"
 * note below.
 */
export const KNOWN_ACCOUNT_LAST4: ReadonlySet<string> = new Set([
  "9167", // Operating
  "9170", // Payroll
  "9183", // Wire
  "9196", // Merchant
  "7175", // Checking
  "0517", // BOA credit card
  "1003", // American Express card (1070 Ameri Express #1003) — added 2026-10-08
]);

/**
 * Extract the account suffix a statement filename refers to, or `null` when the
 * name identifies no known account.
 *
 * Statement names carry the account AND the period as 4-digit groups, in either
 * order — "9167 0626.pdf" (ACCOUNT PERIOD, the current SharePoint export) and
 * "0126 9167.pdf" (PERIOD ACCOUNT, the legacy export). Position is therefore
 * meaningless; only membership in `KNOWN_ACCOUNT_LAST4` identifies the account.
 *
 * Returning `null` rather than guessing is the point: the client's signed rule
 * CAT5 is "when the bank account can't be identified from the file, hold for
 * review — don't assume an account". A wrong account is far more expensive than
 * an unbound row, because it picks the wrong QuickBooks register, the wrong
 * transaction type and the wrong invoice-exemption policy in one stroke.
 */
export function extractAccountFromStatementFilename(
  fileName: string | null | undefined,
): string | null {
  if (!fileName) return null;
  const base = fileName.split(/[/\\]/).pop() ?? fileName;
  const tokens = base.replace(/\.pdf$/i, "").match(/\d{4}/g) ?? [];
  const known = tokens.filter((t) => KNOWN_ACCOUNT_LAST4.has(t));
  if (known.length === 0) return null;
  // Last wins, matching the pre-existing corrected copies: on the rare name
  // carrying two known suffixes the trailing one has always been the account.
  return known[known.length - 1];
}
